import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useRealtime } from "@/lib/realtime";
import DashboardLayout from "@/components/DashboardLayout";
import { ShieldCheck, Filter, Mail, Phone, User as UserIcon, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_TABS = [
  { k: "", l: "All" },
  { k: "open", l: "Open" },
  { k: "resolved", l: "Resolved" },
];

function StatusPill({ v }) {
  const map = {
    open: "bg-rose/10 text-rose border-rose/20",
    resolved: "bg-olive/10 text-olive border-olive/20",
  };
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border ${map[v] || "bg-navy/10 text-navy/60 border-navy/15"}`}>
      {v || "—"}
    </span>
  );
}

function fmtDT(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function AdminGrievances() {
  const { subscribe } = useRealtime();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [status, setStatus] = useState("");
  const [active, setActive] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    const params = new URLSearchParams();
    if (status) params.append("status", status);
    api.get(`/admin/grievances?${params}`).then((r) => {
      setRows(r.data.items || []);
      setTotals(r.data.totals_by_status || {});
    }).catch(() => toast.error("Failed to load grievances"));
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live-refresh when a new grievance arrives
  useEffect(() => subscribe("grievance.new", () => load()), [subscribe, status]);

  const filtered = useMemo(() => rows, [rows]);

  const resolve = async () => {
    if (!active) return;
    setBusy(true);
    try {
      await api.post(`/admin/grievances/${active.ticket_no}/resolve`, { note });
      toast.success(`Ticket ${active.ticket_no} resolved`);
      setActive(null);
      setNote("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to resolve");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DashboardLayout
      title="Grievances"
      subtitle="Direct Seller Redressal · DS Rules 2021"
      actions={
        <div className="hidden md:flex items-center gap-3 text-[11px] uppercase tracking-widest">
          <span className="text-navy/50 inline-flex items-center gap-1"><ShieldCheck size={11} className="text-olive" /> 30-day SLA</span>
        </div>
      }
    >
      {/* Summary counters */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[
          { k: "open", l: "Open", cls: "text-rose", bg: "bg-rose/5 border-rose/20" },
          { k: "resolved", l: "Resolved", cls: "text-olive", bg: "bg-olive/5 border-olive/20" },
          { k: "_total", l: "Total", cls: "text-navy", bg: "bg-cream border-gold/20" },
        ].map((s) => {
          const count = s.k === "_total" ? Object.values(totals).reduce((a, b) => a + b, 0) : (totals[s.k] || 0);
          return (
            <div key={s.k} data-testid={`grv-stat-${s.k}`} className={`border rounded-sm p-4 ${s.bg}`}>
              <div className="text-[10px] uppercase tracking-widest text-navy/50">{s.l}</div>
              <div className={`font-serif text-2xl mt-1 ${s.cls}`}>{count}</div>
            </div>
          );
        })}
      </div>

      {/* Filter tabs */}
      <div className="bg-white border border-border p-3 rounded-sm mb-4 flex gap-2 flex-wrap items-center">
        <Filter size={14} className="text-navy/40 ml-1" />
        {STATUS_TABS.map((t) => (
          <button
            key={t.k}
            data-testid={`grv-tab-${t.k || "all"}`}
            onClick={() => setStatus(t.k)}
            className={`px-3 py-1.5 text-xs border rounded-sm ${status === t.k ? "bg-navy text-gold border-navy" : "border-border text-navy hover:border-gold"}`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {/* Grievances table */}
      <div className="bg-white border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-navy text-white">
            <tr>
              {["Ticket", "Name / Phone", "Category", "Subject", "Filed", "Status", ""].map((h) => (
                <th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-widest font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-navy/50">No grievances{status ? ` in "${status}"` : ""}.</td></tr>
            ) : filtered.map((g) => (
              <tr key={g.ticket_no} data-testid={`grv-row-${g.ticket_no}`} className="border-b border-border last:border-0 hover:bg-cream/40">
                <td className="px-4 py-3 font-mono text-xs text-gold-deep">{g.ticket_no}</td>
                <td className="px-4 py-3 text-navy/80">
                  <div className="font-medium">{g.name}</div>
                  <div className="text-xs text-navy/50">{g.phone}</div>
                </td>
                <td className="px-4 py-3 text-navy/60">{g.category}</td>
                <td className="px-4 py-3 text-navy/70 max-w-[240px] truncate">{g.subject || "—"}</td>
                <td className="px-4 py-3 text-navy/50 text-xs">{fmtDT(g.created_at)}</td>
                <td className="px-4 py-3"><StatusPill v={g.status} /></td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => { setActive(g); setNote(g.resolution_note || ""); }}
                    data-testid={`grv-open-${g.ticket_no}`}
                    className="text-[11px] uppercase tracking-widest px-2 py-1 border border-gold/30 text-gold-deep hover:bg-gold/10 rounded-sm"
                  >
                    Open
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail drawer */}
      {active && (
        <>
          <button
            onClick={() => setActive(null)}
            aria-label="Close"
            className="fixed inset-0 bg-black/40 z-40"
            data-testid="grv-drawer-backdrop"
          />
          <aside
            data-testid="grv-drawer"
            className="fixed top-0 right-0 bottom-0 w-full sm:w-[520px] bg-white z-50 shadow-2xl flex flex-col overflow-y-auto"
          >
            <div className="px-5 py-4 border-b border-navy/10 bg-navy text-white flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-gold/80">Grievance</div>
                <div className="font-mono text-sm">{active.ticket_no}</div>
              </div>
              <button data-testid="grv-drawer-close" onClick={() => setActive(null)} className="text-white/70 hover:text-white p-1"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Info icon={UserIcon} label="Name" value={active.name} />
                <Info icon={Phone} label="Phone" value={active.phone} />
                <Info icon={Mail} label="Email" value={active.email || "—"} />
                <Info icon={UserIcon} label="IBA / Order" value={active.user_code || "—"} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-navy/50 mb-1">Category</div>
                <div className="text-sm text-navy">{active.category}</div>
              </div>
              {active.subject && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-navy/50 mb-1">Subject</div>
                  <div className="text-sm text-navy">{active.subject}</div>
                </div>
              )}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-navy/50 mb-1">Complaint</div>
                <div className="text-sm text-navy/80 whitespace-pre-wrap bg-cream/50 border border-navy/10 rounded-sm p-3">{active.message}</div>
              </div>
              <div className="text-xs text-navy/50">Filed {fmtDT(active.created_at)}</div>
              {active.status === "resolved" ? (
                <div className="bg-olive/5 border border-olive/20 rounded-sm p-3 text-sm">
                  <div className="text-olive font-medium inline-flex items-center gap-1"><CheckCircle2 size={14} /> Resolved</div>
                  <div className="text-navy/70 mt-1">{active.resolution_note || "No note provided."}</div>
                  <div className="text-[10px] text-navy/50 mt-1">Closed {fmtDT(active.resolved_at)}</div>
                </div>
              ) : (
                <div className="border-t border-navy/10 pt-4">
                  <div className="text-[10px] uppercase tracking-widest text-navy/50 mb-1">Resolution note</div>
                  <textarea
                    data-testid="grv-resolve-note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={4}
                    placeholder="Action taken · phone call · refund reference etc."
                    className="w-full border border-navy/15 focus:border-gold focus:outline-none rounded-sm px-3 py-2 text-sm resize-none"
                  />
                  <button
                    onClick={resolve}
                    disabled={busy}
                    data-testid="grv-resolve-btn"
                    className="mt-3 w-full bg-olive text-white hover:bg-olive/90 rounded-sm py-2.5 text-sm uppercase tracking-widest disabled:opacity-50"
                  >
                    {busy ? "Resolving…" : "Mark as Resolved"}
                  </button>
                </div>
              )}
            </div>
          </aside>
        </>
      )}
    </DashboardLayout>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-navy/50 inline-flex items-center gap-1"><Icon size={10} /> {label}</div>
      <div className="text-sm text-navy mt-0.5 break-words">{value}</div>
    </div>
  );
}
